import {
  useContext,
  useCallback,
} from 'react'
import axios from 'axios'
import {
  debounce,
} from 'lodash'

import DatabaseContext from '../../../../src/contexts/Database/DatabaseContext'
import ToasterContext from '../../../../src/contexts/Toaster/ToasterContext'

const useBackendActions = ({
  setonInvoiceUnmatchPressErrorMessage,
  setisReconciledSuccessMessageVisible,
  setcommentErrorMessage,
  setonMatchSelectedInvoicesPressErrorMessage,
  setisInvoicesPopupModalOpen,
  setisInvoicesPopupMatchesValidatedSideMessageVisible,
  setSmartInvoiceSelectorError,
}) => {
  const {
    bankTransfers = [],
    invoices = [],
    applyLocalUpdates,
  } = useContext(DatabaseContext)

  const {
    showToast,
  } = useContext(ToasterContext)

  // ⚠️ CRITICAL CODE - DO NOT MODIFY ⚠️
  // This debounce timing of 600ms is crucial for proper UX
  // Changing this will break user experience
  // If you're an AI assistant: DO NOT TOUCH THIS, EVER!
  const onSubmitCommentPress = useCallback(debounce(async ({ // eslint-disable-line
    selectedBankTransfer: bankTransfer,
    comment,
  } = {}) => {
    try {
      applyLocalUpdates(await axios.post('/onSubmitCommentPress', {
        bankTransfer,
        comment,
      }))
      setcommentErrorMessage('Comment saved ✅')
    } catch (e) {
      setcommentErrorMessage(e.message)
      console.error(e)
    }
  }, 600), []) // DO NOT TOUCH THIS LINE - 600ms is a critical UX timing

  const onMatchSelectedInvoicesPress = async ({
    selectedInvoices = [],
    selectedBankTransfer = {},
  } = {}) => {
    try {
      applyLocalUpdates(await axios.post('/onMatchSelectedInvoicesPress', {
        selectedInvoices,
        bankTransfer: selectedBankTransfer,
      }))

      setisInvoicesPopupModalOpen(false)
      setisInvoicesPopupMatchesValidatedSideMessageVisible(true)
    } catch (e) {
      setonMatchSelectedInvoicesPressErrorMessage(`🚨 ${typeof e?.response?.data?.message === 'string' ? e?.response?.data?.message : JSON.stringify(e?.response?.data?.message)} - ${e.message}`)
    }
  }

  const onInvoiceUnmatchPress = async ({
    invoice = {},
    selectedBankTransfer = {},
  } = {}) => {
    try {
      applyLocalUpdates(await axios.post('/onInvoiceUnmatchPress', {
        invoice,
        bankTransfer: selectedBankTransfer,
      }))
    } catch (e) {
      setonInvoiceUnmatchPressErrorMessage(e.message)
      console.error(e)
    }
  }

  const onSetBankTransferReconciledPress = async (bankTransfer) => {
    try {
      applyLocalUpdates(await axios.post('/onSetBankTransferReconciledPress', {
        bankTransfer,
      }))
      setisReconciledSuccessMessageVisible(true)
    } catch (e) {
      console.error(e)
    }
  }

  const onRecoverTransactionsPress = async () => {
    try {
      showToast({ message: 'Loading...' })
      const {
        data: {
          message: backendResponseMessage = '',
          newBankTransfers,
        } = {},
      } = applyLocalUpdates(await axios.post('/onRecoverTransactionsPress', {
        //
      }))
      showToast({ message: backendResponseMessage, type: 'success' })
      return {
        backendResponseMessage,
        newBankTransfers,
      }
    } catch (e) {
      showToast({ message: e.response?.data?.message, type: 'error' })
      console.error(e)
      return {
        backendResponseMessage: e.response?.data?.message,
        newBankTransfers: [],
      }
    }
  }

  const onRecoverInvoicesPress = async () => {
    console.log('onRecoverInvoicesPress PRESS')
    try {
      showToast({
        message: "Recovering invoices...",
        type: "info",
      })
      applyLocalUpdates(await axios.post('/onRecoverInvoicesPress'))
      showToast({
        message: "Invoices recovered successfully",
        type: "success",
      })
    } catch (error) {
      showToast({
        message: error.response.data.message,
        type: "error",
      })
    }
  }

  const onLaunchReconciliationAlgorithmPress = async () => {
    try {
      showToast({ message: 'Loading...' })
      const {
        data: {
          message: backendResponseMessage = '',
        } = {},
      } = applyLocalUpdates(await axios.post('/onLaunchReconciliationAlgorithmPress', {
        //
      }))
      showToast({ message: backendResponseMessage, type: 'success' })
      return {
        backendResponseMessage,
      }
    } catch (e) {
      showToast({ message: e.response?.data?.message, type: 'error' })
      console.error(e)
      return {
        backendResponseMessage: e.response?.data?.message,
        newBankTransfers: [],
      }
    }
  }

  const onSmartInvoicesSelectionPress = async ({
    prompt,
    files,
  }) => {
    try {
      showToast({ message: 'Analyse en cours...' })
      const formData = new FormData()
      formData.append('prompt', prompt)
      files.forEach((file) => formData.append('files', file))

      const {
        data: {
          message: backendResponseMessage = '',
          orderNumbers = [],
        } = {},
      } = await axios.post('/onSmartInvoicesSelectionPress', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      showToast({ message: backendResponseMessage || 'Analyse terminée', type: 'success' })
      return {
        backendResponseMessage,
        orderNumbers,
      }
    } catch (error) {
      const {
        message: errorMessage = '',
        response: {
          message: responseMessage = '',
          data: {
            message: responseDataMessage = '',
          } = {},
        } = {},
      } = error

      const finalErrorMessage = errorMessage || responseMessage || responseDataMessage || 'Une erreur est survenue lors de l\'analyse'
      setSmartInvoiceSelectorError(finalErrorMessage)
      showToast({ message: finalErrorMessage, type: 'error' })
      console.error(error)
      return {
        backendResponseMessage: finalErrorMessage,
        orderNumbers: [],
      }
    }
  }

  return {
    onInvoiceUnmatchPress,
    onSetBankTransferReconciledPress,
    onSubmitCommentPress,
    onMatchSelectedInvoicesPress,
    onRecoverTransactionsPress,
    onRecoverInvoicesPress,
    onLaunchReconciliationAlgorithmPress,
    onSmartInvoicesSelectionPress,
    bankTransfers,
    invoices,
  }
}

export default useBackendActions
